import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TeamMember } from './types';
import { useToast } from "@/hooks/use-toast";

interface AddTeamMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddMember: (member: TeamMember) => void;
  teamId: string;
}

const AddTeamMemberDialog: React.FC<AddTeamMemberDialogProps> = ({ 
  open, 
  onOpenChange, 
  onAddMember,
  teamId 
}) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const { toast } = useToast();

  const handleSubmit = () => {
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Member name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    if (!role.trim()) {
      toast({
        title: "Error",
        description: "Member role cannot be empty",
        variant: "destructive",
      });
      return;
    }

    const newMember: TeamMember = {
      id: `member-${Date.now()}`,
      name: name.trim(),
      role: role.trim()
    };

    onAddMember(newMember);
    
    // Reset form
    setName('');
    setRole('');
    
    // Close dialog
    onOpenChange(false);
    
    toast({
      title: "Member Added",
      description: `${name} has been added to the team.`,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="dialog-description">
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription id="dialog-description">
            Add a new member to your team
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="member-name">Name</Label>
            <Input 
              id="member-name" 
              placeholder="Member name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="member-role">Role</Label>
            <Input 
              id="member-role" 
              placeholder="Member role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            Add Member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTeamMemberDialog;
